/**
 * <ev-tabs>: the one tabs behavior for every Evident tab set (results tabs, the
 * timeline, the grouped FAQ). WAI-ARIA tabs with automatic activation.
 *
 * Markup contract (Liquid renders the initial state; this script only moves it):
 *   <ev-tabs class="ev-tabs">
 *     <h2 id="heading-{section.id}">...</h2>
 *     <div role="tablist" aria-labelledby="heading-{section.id}" [aria-orientation="vertical"]>
 *       <button type="button" role="tab" id="tab-1" aria-selected="true"  aria-controls="panel-1" tabindex="0">
 *       <button type="button" role="tab" id="tab-2" aria-selected="false" aria-controls="panel-2" tabindex="-1">
 *     </div>
 *     <div role="tabpanel" id="panel-1" aria-labelledby="tab-1" [tabindex="0"]>...</div>
 *     <div role="tabpanel" id="panel-2" aria-labelledby="tab-2" [tabindex="0"] hidden>...</div>
 *   </ev-tabs>
 * Panels take tabindex="0" unless their first content is focusable (Liquid decides).
 *
 * Keys: selection follows focus. Horizontal (default): Left and Right, wrapping.
 * Vertical (aria-orientation="vertical", read on every key press): Up and Down, and
 * Left and Right as well. Home and End go to the first and last tab. Left and Right
 * swap in right-to-left text. Clicking a tab selects it.
 *
 * Event: ev-tabs:change bubbles from <ev-tabs> with detail { index, tab, panel }.
 * Theme editor: selecting a block (a tab, or anything inside a panel) selects that tab.
 */
class EvTabs extends HTMLElement {
  #ready = false;

  /** The first tablist that belongs to this element (not to a nested ev-tabs). */
  get tablist() {
    return [...this.querySelectorAll('[role="tablist"]')].find((list) => list.closest('ev-tabs') === this) || null;
  }

  get tabs() {
    const list = this.tablist;
    return list ? [...list.querySelectorAll('[role="tab"]')].filter((tab) => tab.closest('ev-tabs') === this) : [];
  }

  get selectedIndex() {
    return this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
  }

  connectedCallback() {
    if (this.#ready) return;
    const list = this.tablist;
    if (!list) return;
    this.#ready = true;
    list.addEventListener('click', this.#onClick);
    list.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);

    // Repair a state with no selected tab (or several): keep the first selected one.
    const tabs = this.tabs;
    const index = Math.max(0, this.selectedIndex);
    if (tabs.length) this.select(index, { focus: false, silent: true });
  }

  disconnectedCallback() {
    if (!this.#ready) return;
    this.#ready = false;
    const list = this.tablist;
    list?.removeEventListener('click', this.#onClick);
    list?.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /**
   * Select the tab at `index`: aria-selected, roving tabindex, panel visibility.
   * @param {number} index
   * @param {{ focus?: boolean, silent?: boolean }} [options]
   */
  select(index, { focus = true, silent = false } = {}) {
    const tabs = this.tabs;
    if (!tabs.length) return;
    const next = ((index % tabs.length) + tabs.length) % tabs.length;
    const changed = next !== this.selectedIndex;

    tabs.forEach((tab, i) => {
      const on = i === next;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = this.#panelFor(tab);
      if (panel) panel.hidden = !on;
    });

    const tab = tabs[next];
    if (focus) tab.focus();
    if (changed && !silent) {
      this.dispatchEvent(
        new CustomEvent('ev-tabs:change', { bubbles: true, detail: { index: next, tab, panel: this.#panelFor(tab) } }),
      );
    }
  }

  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    const index = this.tabs.indexOf(tab);
    if (index !== -1) this.select(index);
  };

  #onKeydown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const tabs = this.tabs;
    const current = tabs.indexOf(event.target.closest('[role="tab"]'));
    if (current === -1) return;

    const vertical = this.tablist.getAttribute('aria-orientation') === 'vertical';
    const rtl = getComputedStyle(this.tablist).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = rtl ? 'ArrowRight' : 'ArrowLeft';

    let next = null;
    switch (event.key) {
      case forward:
        next = current + 1;
        break;
      case backward:
        next = current - 1;
        break;
      case 'ArrowDown':
        if (vertical) next = current + 1;
        break;
      case 'ArrowUp':
        if (vertical) next = current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        break;
    }
    if (next === null) return;
    event.preventDefault();
    this.select(next);
  };

  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    // The block's element may be the tab itself (it carries the block attributes) or
    // something inside the tab's panel.
    const index = this.tabs.findIndex((tab) => tab.contains(target) || this.#panelFor(tab)?.contains(target));
    if (index !== -1) this.select(index, { focus: false });
  };
}

if (!customElements.get('ev-tabs')) customElements.define('ev-tabs', EvTabs);
